import { action, computed, makeObservable, observable } from "mobx";
import { create, persist } from "mobx-persist";
import { lang } from "../lang";
import { AlertBoxData, ConfirmBoxData, UploadQueueItem, LoginForm } from "../interface";
import * as HelperSrc from "../HelperSrc";

const config = {
    debug: false,
    api: ""
};

const availableLanguages = ["ja", "en"];

const langKeyMap = {
    ja: "jp",
    en: "us"
};

const API_PATH = `${HelperSrc.URL_BACKEND}`;

const getDefaultCountry = () => {
    // get matching country by availableLanguages and navigator
    const langObj =
        navigator.languages
            .map((langCountryCode) => {
                return langCountryCode.split("-")[0];
            })
            .find((langCode) => availableLanguages.indexOf(langCode) !== -1) || "ja";

    return (langKeyMap[langObj] as string) || "jp";
};

const getDefaultLanguage = () => {
    // get matching language by availableLanguages and navigator
    return (
        navigator.languages
            .map((langCountryCode) => {
                return langCountryCode.split("-")[0];
            })
            .find((langCode) => availableLanguages.indexOf(langCode) !== -1) || "ja"
    );
};

class Store {
    @observable public userid: number | null = null;
    @observable public loaded = false;
    @observable public downloadStatus = HelperSrc.DOWNLOAD_STATUS.DOWNLOADED;
    @observable public downloadingWB: string | null = null;
    @observable public preparingDownload = false;
    @observable public downloadTime = "";
    @observable public isShowDownload = false;
    @observable public loginForm: LoginForm = { username: "", psw: "" };
    @observable loginApiLoading = false;

    // This is the currently selected locale
    // tslint:disable-next-line:variable-name
    @persist @observable public _country: string = getDefaultCountry();
    @persist @observable public _language: string = getDefaultLanguage();

    // This causes a popup
    @observable public alertData: AlertBoxData | null = null;

    @observable public confirm: ConfirmBoxData | null = null;

    @observable public uploadQueue: UploadQueueItem[] = [];

    @observable public activeDirectoryReady = false;

    constructor() {
        makeObservable(this);
    }

    @computed get country() {
        return this._country;
    }
    set country(country: string) {
        this._country = country;
        window.location.reload();
    }

    @computed get language() {
        return this._language;
    }
    set language(language: string) {
        this._language = language;
        lang.setLanguage(store._language);
        window.location.reload();
    }

    @action
    setUserid(userid: number) {
        this.userid = userid;
    }

    @action
    setLoaded(loaded: boolean) {
        this.loaded = loaded;
    }

    @action
    setActiveDirectoryReady(value: boolean) {
        this.activeDirectoryReady = value;
    }

    @action
    setAlertData(data: AlertBoxData) {
        this.alertData = data;
    }

    @action
    setConfirmData(data: ConfirmBoxData) {
        this.confirm = data;
    }

    @action
    setDownloadStatus(value: HelperSrc.DOWNLOAD_STATUS) {
        this.downloadStatus = value;
    }

    @action
    setDownloadingWB(value: string | null) {
        this.downloadingWB = value;
    }

    @action
    setIsShowDownload(value: boolean) {
        this.isShowDownload = value;
    }

    @action
    getIsShowDownload() {
        return this.isShowDownload;
    }

    @action
    setPreparingDownload(value: boolean) {
        this.preparingDownload = value;
    }

    @action
    setDownloadTime(value: string) {
        this.downloadTime = value;
    }

    @action
    setLoginForm(value: LoginForm) {
        this.loginForm = value;
    }

    @action
    clearSession() {
        this.userid = null;
    }

    public basePath = HelperSrc.URL_ROOT;
    public title = "App";
    public apiPath = API_PATH;
}

const hydrate = create({});
const store = new Store();

async function initStore(): Promise<void> {
    await hydrate(`nbb_primaryStore`, store);
}

export { initStore, store, config, API_PATH };
